import React, { useEffect, useRef, useState, useCallback } from "react";
import { api, API, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { compressImage } from "../lib/imageCompress";
import { MessageCircle, Send, Paperclip, Archive, ArchiveRestore, FileText, Loader2, Users, ArrowLeft, Camera } from "lucide-react";
import { ChatPushButton } from "../components/chat/ChatPushButton";
import { MembersDialog, ReadersDialog, ReadTicks, nameColor, roleLabel } from "../components/chat/ChatReads";

const tokenParam = () => {
  const t = localStorage.getItem("access_token");
  return t ? `?auth=${encodeURIComponent(t)}` : "";
};
const fmtTime = (iso) => { try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const isImageMsg = (m) => (m.file_mime ? m.file_mime.startsWith("image/") : /\.(jpe?g|png|webp|gif|heic|heif)$/i.test(m.text || ""));

export default function Chat() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.is_super;
  const [tab, setTab] = useState("attive");
  const [chats, setChats] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [active, setActive] = useState(searchParams.get("chat"));
  const [data, setData] = useState({ messages: [], members: [], chat: null });
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [infoMsg, setInfoMsg] = useState(null);
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const [lightbox, setLightbox] = useState(null);
  const endRef = useRef(null);

  const loadChats = useCallback(async () => {
    try {
      const { data } = await api.get(`/chats?archived=${tab === "archiviate"}`);
      setChats(data.chats || []);
    } catch (e) { /* silent */ }
  }, [tab]);

  const loadMessages = useCallback(async (cid) => {
    if (!cid) return;
    try {
      const { data } = await api.get(`/chats/${cid}/messages`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => { loadChats(); }, [loadChats]);
  useEffect(() => { const t = setInterval(() => { loadChats(); if (active) loadMessages(active); }, 5000); return () => clearInterval(t); }, [loadChats, loadMessages, active]);
  useEffect(() => { if (active) loadMessages(active); }, [active, loadMessages]);
  const msgCount = (data.messages || []).length;
  useEffect(() => {
    const el = endRef.current?.parentElement;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [msgCount, active]);

  const openChat = (c) => { setActive(c.id); setData({ messages: [], members: [], chat: c }); };
  const closeChat = () => { setActive(null); if (searchParams.get("chat")) setSearchParams({}, { replace: true }); };

  const send = async () => {
    if (!text.trim() || !active) return;
    setSending(true);
    try { await api.post(`/chats/${active}/messages`, { text }); setText(""); await loadMessages(active); await loadChats(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setSending(false); }
  };

  const sendFile = async (f, isPhoto) => {
    if (!f || !active) return;
    setUploading(true);
    try {
      let processed = await compressImage(f, isPhoto ? { maxDim: 1600, quality: 0.7, force: true } : undefined);
      if (isPhoto && processed.type === "image/jpeg") {
        const stamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, "");
        processed = new File([processed], `Foto_${stamp}.jpg`, { type: processed.type || "image/jpeg" });
      }
      const fd = new FormData(); fd.append("file", processed);
      await api.post(`/chats/${active}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await loadMessages(active);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };
  const onPhotoLoad = () => {
    const el = endRef.current?.parentElement;
    if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 500) el.scrollTo({ top: el.scrollHeight });
  };
  const onFile = async (e) => {
    const fs = [...(e.target.files || [])]; e.target.value = "";
    if (!fs.length || !active) return;
    const imgs = fs.filter((f) => f.type.startsWith("image/"));
    if (fs.length === 1) { await sendFile(fs[0], false); return; }
    if (imgs.length > 20) { toast.error("Puoi inviare al massimo 20 foto insieme"); return; }
    setUploading(true);
    try {
      if (imgs.length) {
        const fd = new FormData();
        for (const f of imgs) fd.append("files", await compressImage(f, { maxDim: 1600, quality: 0.7 }));
        await api.post(`/chats/${active}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      for (const f of fs.filter((x) => !x.type.startsWith("image/"))) {
        const fd = new FormData(); fd.append("file", f);
        await api.post(`/chats/${active}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      await loadMessages(active);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };
  const onPhoto = async (e) => { const f = e.target.files?.[0]; e.target.value = ""; await sendFile(f, true); };

  const doArchive = async (archive) => {
    try { await api.post(`/chats/${active}/${archive ? "archive" : "unarchive"}`); toast.success(archive ? "Chat archiviata" : "Chat ripristinata"); setActive(null); await loadChats(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const chat = data.chat;

  return (
    <div className="h-[calc(100dvh-11rem)] md:h-[calc(100vh-7rem)] flex gap-4" data-testid="chat-page">
      {/* Elenco chat */}
      <div className={`${active ? "hidden md:flex" : "flex"} w-full md:w-80 flex-shrink-0 card-shell flex-col overflow-hidden`} data-testid="chat-list-panel">
        <div className="p-3 border-b flex gap-1" style={{ borderColor: "var(--border)" }}>
          <button onClick={() => { setTab("attive"); setActive(null); }} className={tab === "attive" ? "btn-primary text-xs flex-1" : "btn-secondary text-xs flex-1"} data-testid="chat-tab-attive">Attive</button>
          <button onClick={() => { setTab("archiviate"); setActive(null); }} className={tab === "archiviate" ? "btn-primary text-xs flex-1" : "btn-secondary text-xs flex-1"} data-testid="chat-tab-archiviate">Archiviate</button>
        </div>
        <div className="flex-1 overflow-auto">
          {chats.length === 0 && <div className="p-4 text-sm text-center" style={{ color: "var(--text-muted)" }} data-testid="chat-empty">Nessuna chat</div>}
          {chats.map((c) => (
            <button key={c.id} onClick={() => openChat(c)} data-testid={`chat-item-${c.id}`}
              className="w-full text-left px-3 py-2.5 border-b hover:bg-black/[0.03] transition-colors flex items-center gap-2"
              style={{ borderColor: "var(--border)", background: active === c.id ? "var(--surface-muted)" : "transparent" }}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{c.titolo}</div>
                <div className="text-xs truncate" style={{ color: "var(--text-muted)" }}>{c.last_message || "—"}</div>
              </div>
              {c.unread > 0 && (
                <span className="text-[11px] font-bold text-white rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center" style={{ background: "#1B5E20" }} data-testid={`chat-unread-${c.id}`}>{c.unread}</span>
              )}
            </button>
          ))}
        </div>
        <div className="border-t" style={{ borderColor: "var(--border)" }}><ChatPushButton /></div>
      </div>

      {/* Conversazione */}
      <div className={`${active ? "flex" : "hidden md:flex"} flex-1 min-w-0 card-shell flex-col overflow-hidden`} data-testid="chat-conversation-panel">
        {!active ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center" style={{ color: "var(--text-muted)" }}>
            <MessageCircle className="w-10 h-10 mb-2 opacity-40" />
            <div className="text-sm">Seleziona una chat</div>
          </div>
        ) : (
          <>
            <div className="p-3 border-b flex items-center gap-3" style={{ borderColor: "var(--border)" }}>
              <button type="button" onClick={closeChat} className="md:hidden p-1.5 -ml-1 rounded-md hover:bg-black/5" aria-label="Torna all'elenco" data-testid="chat-back"><ArrowLeft className="w-5 h-5" /></button>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate" data-testid="chat-title">{chat?.titolo}</div>
                <div className="text-xs flex items-center gap-1" style={{ color: "var(--text-muted)" }}>
                  <button type="button" onClick={() => setShowMembers(true)} className="inline-flex items-center gap-1 hover:underline" data-testid="chat-members-btn"><Users className="w-3 h-3" /> {(data.members || []).length || chat?.members_count || chats.find((c) => c.id === active)?.members_count || 0} membri</button>
                </div>
              </div>
              {isAdmin && (tab === "attive"
                ? <button onClick={() => doArchive(true)} className="btn-secondary text-xs" data-testid="chat-archive"><Archive className="w-3.5 h-3.5" /> Archivia</button>
                : <button onClick={() => doArchive(false)} className="btn-secondary text-xs" data-testid="chat-unarchive"><ArchiveRestore className="w-3.5 h-3.5" /> Ripristina</button>)}
            </div>

            <div className="flex-1 overflow-auto p-4 space-y-2" style={{ background: "var(--bg)" }}>
              {(data.messages || []).map((m) => {
                const mine = m.user_id === user?.id;
                const isSys = m.type === "system";
                if (isSys) return (
                  <div key={m.id} className="text-center my-2">
                    <span className="inline-block text-xs px-3 py-1.5 rounded-lg whitespace-pre-wrap text-left" style={{ background: "var(--surface-muted)", color: "var(--text-muted)" }} data-testid={`chat-msg-${m.id}`}>{m.text}</span>
                  </div>
                );
                const author = (data.authors || data.members || []).find((u) => u.id === m.user_id);
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
                    <div className="max-w-[75%] rounded-xl px-3 py-2 text-sm" style={{ background: mine ? "#1B5E20" : "#fff", color: mine ? "#fff" : "var(--text)", border: mine ? "none" : "1px solid var(--border)" }}>
                      {!mine && (
                        <div className="text-[12px] font-bold mb-0.5 flex items-baseline gap-1.5" style={{ color: m.user_id ? nameColor(m.user_id) : "#64748b" }} data-testid={`chat-author-${m.id}`}>
                          {m.user_name || "Sistema"}{author && <span className="text-[10px] font-medium" style={{ color: "var(--text-muted)" }}>{roleLabel(author.role)}</span>}
                        </div>
                      )}
                      {m.type === "photos" && m.file_ids?.length ? (
                        <div className={`grid gap-1 -mx-1 -mt-0.5 mb-0.5 ${m.file_ids.length === 1 ? "grid-cols-1" : "grid-cols-2"}`} style={{ width: 240, maxWidth: "100%" }} data-testid={`chat-album-${m.id}`}>
                          {m.file_ids.slice(0, 4).map((fid, i) => (
                            <button key={fid} type="button" onClick={() => setLightbox({ ...m, file_id: fid, idx: i })} className="relative block aspect-square overflow-hidden rounded-md" data-testid={`chat-album-photo-${m.id}-${i}`}>
                              <img src={`${API}/files/${fid}/download${tokenParam()}`} alt={`Foto ${i + 1}`} loading="lazy" onLoad={onPhotoLoad} className="w-full h-full object-cover" style={{ background: "#0001" }} />
                              {i === 3 && m.file_ids.length > 4 && (
                                <span className="absolute inset-0 flex items-center justify-center text-2xl font-bold text-white" style={{ background: "rgba(0,0,0,.5)" }}>+{m.file_ids.length - 4}</span>
                              )}
                            </button>
                          ))}
                        </div>
                      ) : m.type === "file" && m.file_id && isImageMsg(m) ? (
                        <button type="button" onClick={() => setLightbox(m)} className="block -mx-1 -mt-0.5 mb-0.5" data-testid={`chat-photo-${m.id}`}>
                          <img src={`${API}/files/${m.file_id}/download${tokenParam()}`} alt={m.text || "Foto"} loading="lazy" onLoad={onPhotoLoad}
                            className="rounded-lg max-h-64 w-auto max-w-full object-cover" style={{ minWidth: 120, minHeight: 80, background: "#0001" }} />
                        </button>
                      ) : m.type === "file" && m.file_id ? (
                        <a href={`${API}/files/${m.file_id}/download${tokenParam()}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 underline" style={{ color: mine ? "#fff" : "#1B5E20" }}>
                          <FileText className="w-4 h-4" /> {m.text || "Allegato"}
                        </a>
                      ) : <div className="whitespace-pre-wrap break-words">{m.text}</div>}
                      <div className="text-[10px] mt-0.5 flex items-center justify-end gap-2" style={{ opacity: 0.75 }}>
                        <span>{fmtTime(m.created_at)}</span>
                        {m.user_id && <ReadTicks m={m} members={data.members || []} mine={mine} onOpen={() => setInfoMsg(m)} />}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={endRef} />
            </div>
            {showMembers && <MembersDialog members={data.members || []} onClose={() => setShowMembers(false)} />}
            {infoMsg && <ReadersDialog m={(data.messages || []).find((x) => x.id === infoMsg.id) || infoMsg} members={data.members || []} onClose={() => setInfoMsg(null)} />}
            {lightbox && (
              <div className="fixed inset-0 z-50 flex flex-col items-center justify-center p-3" style={{ background: "rgba(0,0,0,.9)" }} onClick={() => setLightbox(null)} data-testid="chat-photo-lightbox">
                <img src={`${API}/files/${lightbox.file_id}/download${tokenParam()}`} alt="Foto" className="max-w-full max-h-[85vh] object-contain rounded" />
                <div className="mt-3 flex items-center gap-3 text-sm text-white">
                  {lightbox.file_ids?.length > 1 && (
                    <>
                      <button type="button" onClick={(e) => { e.stopPropagation(); const i = (lightbox.idx - 1 + lightbox.file_ids.length) % lightbox.file_ids.length; setLightbox({ ...lightbox, idx: i, file_id: lightbox.file_ids[i] }); }} className="px-3 py-1 rounded bg-white/15" data-testid="chat-photo-prev">‹</button>
                      <span data-testid="chat-photo-counter">{lightbox.idx + 1}/{lightbox.file_ids.length}</span>
                      <button type="button" onClick={(e) => { e.stopPropagation(); const i = (lightbox.idx + 1) % lightbox.file_ids.length; setLightbox({ ...lightbox, idx: i, file_id: lightbox.file_ids[i] }); }} className="px-3 py-1 rounded bg-white/15" data-testid="chat-photo-next">›</button>
                    </>
                  )}
                  <span className="opacity-80">{lightbox.user_name} · {fmtTime(lightbox.created_at)}</span>
                  <a href={`${API}/files/${lightbox.file_id}/download${tokenParam()}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="underline font-semibold" data-testid="chat-photo-open">Apri originale</a>
                </div>
              </div>
            )}

            {chat?.archived ? (
              <div className="p-3 border-t text-center text-xs" style={{ borderColor: "var(--border)", color: "var(--text-muted)" }}>Chat archiviata — sola lettura</div>
            ) : (
              <div className="p-3 border-t flex items-center gap-2" style={{ borderColor: "var(--border)" }}>
                <input ref={fileRef} type="file" multiple accept="image/*,application/pdf" className="hidden" onChange={onFile} data-testid="chat-file-input" />
                <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhoto} data-testid="chat-camera-input" />
                <button onClick={() => camRef.current?.click()} disabled={uploading} className="p-2 rounded-lg hover:bg-black/5" title="Scatta e invia una foto" data-testid="chat-camera">
                  <Camera className="w-5 h-5" />
                </button>
                <button onClick={() => fileRef.current?.click()} disabled={uploading} className="p-2 rounded-lg hover:bg-black/5" title="Allega immagine o PDF" data-testid="chat-attach">
                  {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}
                </button>
                <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Scrivi un messaggio…" className="flex-1 px-3 py-2 text-sm rounded-lg border outline-none" style={{ borderColor: "var(--border)", background: "#fff" }} data-testid="chat-input" />
                <button onClick={send} disabled={sending || !text.trim()} className="btn-primary disabled:opacity-50" data-testid="chat-send">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
